import * as THREE from "three";

export interface CameraKeyframe {
  /** Position in world space */
  position: THREE.Vector3Tuple;
  /** Look-at target in world space */
  target: THREE.Vector3Tuple;
  /** Field of view in degrees */
  fov: number;
  /** Optional roll in radians */
  roll?: number;
}

/**
 * A camera that travels along a Catmull-Rom spline driven by normalized scroll progress.
 *
 * Why this matters: in the legacy site each section had its own camera at (0,0,5) looking at
 * (0,0,0). There was no real "journey" — sections were isolated. With a spline path, scroll
 * physically moves the camera through the 3D world, creating cinematic continuity between
 * scenes. Each section registers a keyframe; we interpolate position, target, and FOV.
 */
export class JourneyCamera {
  readonly camera: THREE.PerspectiveCamera;
  private posCurve!: THREE.CatmullRomCurve3;
  private targetCurve!: THREE.CatmullRomCurve3;
  private fovs: number[] = [];
  private rolls: number[] = [];
  private progress = 0;
  private currentLookAt = new THREE.Vector3();

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(35, aspect, 0.1, 200);
  }

  setKeyframes(kfs: CameraKeyframe[]): void {
    if (kfs.length < 2) {
      throw new Error("[JourneyCamera] Need at least 2 keyframes to build a path");
    }
    this.posCurve = new THREE.CatmullRomCurve3(
      kfs.map((k) => new THREE.Vector3(...k.position)),
      false,
      "catmullrom",
      0.5,
    );
    this.targetCurve = new THREE.CatmullRomCurve3(
      kfs.map((k) => new THREE.Vector3(...k.target)),
      false,
      "catmullrom",
      0.5,
    );
    this.fovs = kfs.map((k) => k.fov);
    this.rolls = kfs.map((k) => k.roll ?? 0);
  }

  /**
   * Drive the camera with normalized scroll progress in [0, 1].
   */
  setProgress(t: number): void {
    this.progress = THREE.MathUtils.clamp(t, 0, 1);
    if (!this.posCurve) return;

    const pos = this.posCurve.getPointAt(this.progress);
    const target = this.targetCurve.getPointAt(this.progress);
    this.camera.position.copy(pos);
    this.currentLookAt.lerp(target, 0.15); // tiny smoothing so micro-jumps soften
    this.camera.lookAt(this.currentLookAt);

    // Piecewise-linear FOV interpolation between keyframes
    const idxF = this.progress * (this.fovs.length - 1);
    const i = Math.floor(idxF);
    const f = idxF - i;
    const a = this.fovs[i] ?? 35;
    const b = this.fovs[i + 1] ?? a;
    this.camera.fov = THREE.MathUtils.lerp(a, b, f);

    const ra = this.rolls[i] ?? 0;
    const rb = this.rolls[i + 1] ?? ra;
    this.camera.rotation.z = THREE.MathUtils.lerp(ra, rb, f);

    this.camera.updateProjectionMatrix();
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Debug helper: get a visualization of the path */
  getPathLine(divisions = 200): THREE.Line {
    const pts = this.posCurve.getPoints(divisions);
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    const mat = new THREE.LineBasicMaterial({ color: 0xc9a24b });
    return new THREE.Line(geo, mat);
  }
}
